import {Injectable} from '@angular/core';
import {RequesterService} from "../../../shared/services/requester/requester.service";
import {DbResponse} from "../../../shared/interceptors/db-response";
import {BehaviorSubject} from "rxjs";
import {TypeUtils} from "../../../shared/utils/type-utils";
import {SecurityIssue} from "./security-issue";
import {SecurityFileDiffMapper} from "./security-file-diff-mapper";
import {SecurityScheduleMapper} from "./security-schedule-mapper";
import {SecurityIgnoredIssue} from "./security-ignored-issue";
import * as fromStore from '../../../store';
import {map} from "rxjs/operators";
import {Store} from "@ngrx/store";
import {DbStoreState} from '../../../store/reducers';

@Injectable()
export class SecurityService {

  private currentWorkspaceId: any;
  private ownerWorkspaceId: any;

  constructor(
    private _requester: RequesterService,
    private _store: Store<DbStoreState>
  ) {
    this._store.select(fromStore.getWorkspaceIdEntities).subscribe( workspaceIdEntities => {
      this.ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
      this.currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this.ownerWorkspaceId;
    });
  }

  public runSecurityScan(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.security
      .put(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/scan/run`)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success && TypeUtils.isEmpty(response.data)) {
          response.data = null;
        }

        return response;
      })));
  }

  public getSecurityScan(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>(this._requester.security
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/logs`, null, true)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success && TypeUtils.isEmpty(response.data)) {
          response.data = null;
        }

        return response;
      })));
  }

  public getSecurityIgnoredIssues(websiteId: number): BehaviorSubject<DbResponse> {
    return this._requester.security
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/issues/ignored`, null, true);
  }

  public ignoreIssue(websiteId: number, issue: SecurityIssue): BehaviorSubject<DbResponse> {
    const postData: any = {
      file_path: issue.file_path,
      status: issue.status,
      type: issue.type, // core_issues,theme_issues,plugin_issues’,
      slug: issue.slug,
      version: issue.version
    };

    return <BehaviorSubject<DbResponse>>(this._requester.security
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/issue/ignore`, null, postData)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success && TypeUtils.isEmpty(response.data)) {
          response.data = null;
        }

        return response;
      })));
  }

  public restoreIssue(websiteId: number, ignoredIssueId: string): BehaviorSubject<DbResponse> {
    return this._requester.security
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/ignored/${ignoredIssueId}/restore`);
  }

  public getFileDiff(websiteId: number, securityIssue: SecurityIssue): BehaviorSubject<DbResponse> {
    const postData = {
      domain_id: websiteId,
      file_path: securityIssue.file_path,
      version: securityIssue.version,
      slug: securityIssue.slug,
      type: securityIssue.diffType // plugin, theme, core
    };
    return <BehaviorSubject<DbResponse>>(this._requester.security
      .post(`workspaces/${this.currentWorkspaceId}/file/diff`, null, postData)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success) {
          response.data = SecurityFileDiffMapper.map(response.data, securityIssue.status);
        }

        return response;
      })));
  }

  public restoreFile(websiteId: number, securityIssue: SecurityIssue): BehaviorSubject<DbResponse> {
    if (securityIssue.status.toLowerCase() === 'added') {
      return this._requester.security
        .delete(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/file/delete?file_path=${securityIssue.file_path}`);
    }
    else {
      return this._requester.security
        .post(`workspaces/${this.currentWorkspaceId}/restore/original`, null, {
          domain_id: websiteId,
          file_path: securityIssue.file_path,
          version: securityIssue.version,
          slug: securityIssue.slug,
          type: securityIssue.diffType // plugin, theme, core
        });
    }
  }

  public getSecuritySettings(websiteId: number): BehaviorSubject<DbResponse> {
    return this._requester.security
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/settings`, null, true);
  }

  public updateSecuritySettings(websiteId: number, settingsData: any): BehaviorSubject<DbResponse> {
    return this._requester.security
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/settings`, null, settingsData);
  }

  public getSecuritySchedule(websiteId: number): BehaviorSubject<DbResponse> {
    return this._requester.security
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/settings`, null, true);
  }

  public getSecurityAutoupdate(websiteId: number): BehaviorSubject<DbResponse> {
    return this._requester.security
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/settings`, null, true);
  }

  public updateSecuritySchedule(websiteId: number, scheduleData: any): BehaviorSubject<DbResponse> {
    return this._requester.security
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/settings`, null, SecurityScheduleMapper.generateRequestData(scheduleData));
  }

  public updateSecurityAutoupdate(websiteId: number, autoupdateData: any): BehaviorSubject<DbResponse> {
    return this._requester.security
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/settings`, null, autoupdateData);
  }

  public bulkIgnoreIssue(websiteId: number, issues: SecurityIssue[]): BehaviorSubject<DbResponse> {
    const postData = {data: JSON.stringify(issues)};
    return <BehaviorSubject<DbResponse>>(this._requester.security
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/issue/bulk/ignore`, null, postData)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success && TypeUtils.isEmpty(response.data)) {
          response.data = null;
        }

        return response;
      })));
  }

  public bulkRestoreIssue(websiteId: number, ignoredIssues: any[]): BehaviorSubject<DbResponse> {
    const ignoredIds = ignoredIssues.reduce((ignoredIssuesData: any, issue: SecurityIgnoredIssue) => {
      ignoredIssuesData.push(issue.id);
      return ignoredIssuesData;
    }, []);
    const postData = {ids: JSON.stringify(ignoredIds)};
    return <BehaviorSubject<DbResponse>>(this._requester.security
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/bulk/restore`, null, postData)
      .pipe(map((response: DbResponse): DbResponse => {
        if (response && response.success && TypeUtils.isEmpty(response.data)) {
          response.data = null;
        }

        return response;
      })));
  }

}
